import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';

import styled from '@emotion/styled';
import { startRegistration } from '@simplewebauthn/browser';

import { PrimaryBlackButton } from '../../../components/Button/Buttons';
import HetheraLogo from '../../../components/CompanyLogo/HetheraLogo';
import usePasskeyApi from '../../../hooks/auth/passkeys/usePasskeyApi';
import { browserSupportsWebAuthn, passkeyErrorMessage, UNSUPPORTED } from '../../../hooks/auth/passkeys/webauthn';
import { IEnrolledResponse, ISecondStepResponse } from '../../../models/auth/signIn.model';
import { deviceName } from '../SignIn/PasskeyStep';
import RecoveryCodes from './RecoveryCodes';

// /account/passkeys/enroll#t=<token>: the first passkey needs the password
// and the single-use link made by scripts/admin_passkey_enroll_link.py (ops
// spec D18). The token is read from the fragment, kept in memory and wiped
// from the address bar at once.
const EnrollPasskey = () => {
  const api = usePasskeyApi();
  const navigate = useNavigate();
  const [linkToken, setLinkToken] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(browserSupportsWebAuthn() ? null : UNSUPPORTED);
  const [codes, setCodes] = useState<string[] | null>(null);

  useEffect(() => {
    const token = new URLSearchParams(window.location.hash.slice(1)).get('t');
    if (token) setLinkToken(token);
    window.history.replaceState(null, '', window.location.pathname);
  }, []);

  const enroll = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!linkToken) return;
    setError(null);
    setBusy(true);
    try {
      const step = await api<ISecondStepResponse>('api/v1/auth/admin/login', { email, password });
      if (step.second_step !== 'enroll') {
        throw new Error('This account already has a passkey. Sign in, then add more from Passkeys.');
      }
      const optionsJSON = await api<any>('api/v1/auth/admin/passkey/register/options', {
        ticket: step.ticket,
        enrollment_token: linkToken,
      });
      const credential = await startRegistration({ optionsJSON });
      const result = await api<IEnrolledResponse>('api/v1/auth/admin/passkey/register/verify', {
        ticket: step.ticket,
        enrollment_token: linkToken,
        credential,
        name: deviceName(),
      });
      setLinkToken(null);
      setCodes(result.recovery_codes);
    } catch (err) {
      setError(passkeyErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Page>
      <CardBox>
        <HetheraLogo size={36} />
        {codes ? (
          <RecoveryCodes codes={codes} onDone={() => navigate('/account/signin', { replace: true })} doneLabel="I've saved them, sign in" />
        ) : !linkToken ? (
          <p>This page needs an enrollment link. Ask whoever runs the Hethera servers for a new one.</p>
        ) : (
          <form onSubmit={enroll}>
            <h2>Add your passkey</h2>
            <p>Sign in with your password, then use your fingerprint, face or device PIN to create a passkey on this device.</p>
            <input type='email' autoComplete='username' placeholder='Email' value={email} onChange={(e) => setEmail(e.target.value)} required />
            <input type='password' autoComplete='current-password' placeholder='Password' value={password} onChange={(e) => setPassword(e.target.value)} required />
            <PrimaryBlackButton type='submit' className='large' disabled={busy || !browserSupportsWebAuthn()}>
              {busy ? 'Waiting for your passkey…' : 'Create passkey'}
            </PrimaryBlackButton>
          </form>
        )}
        {error && <ErrorText role='alert'>{error}</ErrorText>}
      </CardBox>
    </Page>
  );
};

export default EnrollPasskey;

const Page = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px 16px;
  background: var(--ground);
`;

const CardBox = styled.div`
  width: 100%;
  max-width: 440px;
  background: white;
  border: 1px solid ${({ theme }) => theme.palette.borderColor};
  border-radius: var(--radius);
  padding: 28px 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;

  h2 {
    margin: 0;
  }
  p {
    margin: 0;
    font-size: 14px;
    color: ${({ theme }) => theme.palette.grayTextColor};
  }
  form {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
  input {
    border: 1px solid ${({ theme }) => theme.palette.borderColor};
    border-radius: 12px;
    padding: 12px 14px;
    font: inherit;
  }
`;

const ErrorText = styled.div`
  color: ${({ theme }) => theme.palette.errorRed};
  font-size: 14px;
`;
